import { axiosInstance } from "./index";

// Block Status Types
export enum BlockStatus {
	CANCELLED = "Cancelled",
	COMPLETE = "Complete",
	IN_PROGRESS = "In Progress",
}

// Block Visibility Types
export enum BlockVisibility {
	AGENCY_ONLY = "AGENCY_ONLY",
	CLIENT_VISIBLE = "CLIENT_VISIBLE",
	CREATOR_VISIBLE = "CREATOR_VISIBLE",
}

// Define the block data interface matching the backend model
export interface Block {
	id: string;
	campaignId: string;
	name: string;
	status: BlockStatus;
	visibility?: BlockVisibility | null;
	type: BlockType;
	active: boolean;
	target: string[];
	rootId?: string;
	engagementDeliverableId?: string;
	hasCet?: boolean;
	createdAt: string;
	completedAt?: string | null;
	createdBy?: string;
}

export interface BlockMarketExpectations {
	blockId: string;
	numCreators?: number;
	numAssets?: number;
}

export type DeliverableCompletionIssueReason = "missing" | "incomplete" | "rejected";
export type DeliverableCompletionFixAction = "upload" | "add_link" | "view_feedback";

export interface DeliverableCompletionIssue {
	assetBlockId: string;
	assetType: "CONCEPT" | "VIDEO" | "LINK";
	label: string;
	reason: DeliverableCompletionIssueReason;
	message: string;
	fixAction: DeliverableCompletionFixAction;
	resourceId?: string;
	version?: number;
	reviewedAt?: string;
	reviewerName?: string;
}

export interface DeliverableCompletionReadyAsset {
	assetBlockId: string;
	assetType: "CONCEPT" | "VIDEO" | "LINK";
	label: string;
	resourceId: string;
	reviewerName?: string;
	reviewedAt?: string;
	url?: string;
}

export interface DeliverableCompletionCheck {
	eligible: boolean;
	issues: DeliverableCompletionIssue[];
	readyAssets?: DeliverableCompletionReadyAsset[];
}

export interface MarketBlock extends Block {
	marketExpectations?: BlockMarketExpectations;
}

type BlockResponse = Block & { marketExpectations?: BlockMarketExpectations };

export enum BlockType {
	MARKET = "MARKET",
	CREATOR_FINDING = "CREATOR_FINDING",
	CREATOR_LIST = "CREATOR_LIST",
	CREATOR_OUTREACH = "CREATOR_OUTREACH",
	CREATOR_GROUPING = "CREATOR_GROUPING",
	CONTENT_EXECUTION = "CONTENT_EXECUTION",
	CREATOR = "CREATOR",
	DELIVERABLE = "DELIVERABLE",
	CONCEPT = "CONCEPT",
	VIDEO = "VIDEO",
	LINK = "LINK",
}

// Extended interface for table display (adds key for Ant Design Table)
export interface BlockData extends Block {
	key: string; // Required by Ant Design Table
	marketExpectations?: BlockMarketExpectations;
}

// Model validation
export function validateBlock(block: Block): string {
	if (!block.name || block.name.length === 0) {
		return "Block name is required";
	}
	if (!block.campaignId || block.campaignId.length === 0) {
		return "Campaign ID is required";
	}
	return "";
}

// Helper function to get status color for UI
export function getBlockStatusColor(status: BlockStatus): string {
	switch (status) {
		case BlockStatus.COMPLETE:
			return "green";
		case BlockStatus.IN_PROGRESS:
			return "blue";
		case BlockStatus.CANCELLED:
			return "red";
		default:
			return "default";
	}
}

// Block API service
export const blockAPI = {
	// Get all blocks for a campaign
	getByCampaignId: async (campaignId: string): Promise<BlockData[]> => {
		try {
			const response = await axiosInstance.get<BlockResponse[]>(`/campaign/${campaignId}/blocks`);
			// Transform backend data to include key for Ant Design Table
			const blockData: BlockData[] = response.data.map((block: BlockResponse) => ({
				...block,
				key: block.id, // Use block id as key
				marketExpectations: block.marketExpectations,
			}));
			return blockData;
		} catch (error) {
			console.error(`Error fetching blocks for campaign ${campaignId}:`, error);
			throw new Error("Failed to fetch blocks");
		}
	},

	// Get block by ID
	getById: async (campaignId: string, blockId: string): Promise<BlockData> => {
		try {
			const response = await axiosInstance.get<BlockResponse>(`/campaign/${campaignId}/blocks/${blockId}`);
			const blockData: BlockData = {
				...response.data,
				key: response.data.id,
				marketExpectations: response.data.marketExpectations,
			};
			return blockData;
		} catch (error) {
			console.error(`Error fetching block with ID ${blockId}:`, error);
			throw new Error(`Failed to fetch block with ID ${blockId}`);
		}
	},

	// Create new block
	create: async (
		campaignId: string,
		blockData: Omit<Block, "id" | "createdAt" | "campaignId">,
	): Promise<BlockData> => {
		try {
			const response = await axiosInstance.post<{ message: string; block: BlockResponse }>(
				`/campaign/${campaignId}/blocks`,
				{
					...blockData,
					campaignId,
				},
			);
			const block = response.data.block;
			const newBlockData: BlockData = {
				...block,
				key: block.id,
				marketExpectations: block.marketExpectations,
			};
			return newBlockData;
		} catch (error) {
			console.error("Error creating block:", error);
			throw new Error("Failed to create block");
		}
	},

	// Update block
	update: async (campaignId: string, blockId: string, blockData: Partial<Block>): Promise<BlockData> => {
		try {
			const response = await axiosInstance.put<BlockResponse>(
				`/campaign/${campaignId}/blocks/${blockId}`,
				blockData,
			);
			const updatedBlockData: BlockData = {
				...response.data,
				key: response.data.id,
				marketExpectations: response.data.marketExpectations,
			};
			return updatedBlockData;
		} catch (error) {
			console.error(`Error updating block with ID ${blockId}:`, error);
			throw new Error(`Failed to update block with ID ${blockId}`);
		}
	},

	updateDeliverableStatus: async (
		campaignId: string,
		blockId: string,
		status: BlockStatus.COMPLETE | BlockStatus.IN_PROGRESS,
	): Promise<BlockData> => {
		const response = await axiosInstance.patch<BlockResponse>(`/campaign/${campaignId}/blocks/${blockId}/status`, {
			status,
		});
		return { ...response.data, key: response.data.id };
	},

	getDeliverableCompletionCheck: async (campaignId: string, blockId: string): Promise<DeliverableCompletionCheck> => {
		const response = await axiosInstance.get<DeliverableCompletionCheck>(
			`/campaign/${campaignId}/blocks/${blockId}/completion-check`,
		);
		return response.data;
	},

	// Update only market expectations for a MARKET block
	updateMarket: async (
		campaignId: string,
		blockId: string,
		marketData: Partial<BlockMarketExpectations>,
	): Promise<BlockData> => {
		try {
			const response = await axiosInstance.put<BlockResponse>(
				`/campaign/${campaignId}/blocks/${blockId}`,
				marketData,
			);
			const updatedBlockData: BlockData = {
				...response.data,
				key: response.data.id,
				marketExpectations: response.data.marketExpectations,
			};
			return updatedBlockData;
		} catch (error) {
			console.error(`Error updating market for block ID ${blockId}:`, error);
			throw new Error(`Failed to update market for block ID ${blockId}`);
		}
	},

	// Delete block
	delete: async (campaignId: string, blockId: string): Promise<void> => {
		try {
			await axiosInstance.delete(`/campaign/${campaignId}/blocks/${blockId}`);
		} catch (error) {
			console.error(`Error deleting block with ID ${blockId}:`, error);
			throw new Error(`Failed to delete block with ID ${blockId}`);
		}
	},

	// Add market blocks (creates workflow blocks for a market)
	addMarket: async (
		campaignId: string,
		marketName: string,
		numCreators: number = 0,
		numAssets: number = 0,
	): Promise<BlockData[]> => {
		try {
			const response = await axiosInstance.post<{ blocks: BlockResponse[] }>(`/campaign/${campaignId}/market`, {
				marketName,
				numCreators,
				numAssets,
			});
			const blockData: BlockData[] = response.data.blocks.map((block: BlockResponse) => ({
				...block,
				key: block.id,
				marketExpectations: block.marketExpectations,
			}));
			return blockData;
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || error?.message || "Failed to add market blocks";
			const err = new Error(errorMessage);
			(err as any).response = error?.response;
			throw err;
		}
	},

	// Remove market blocks from campaign
	removeMarket: async (campaignId: string, country: string): Promise<{ message: string }> => {
		try {
			const response = await axiosInstance.delete(`/campaign/${campaignId}/market/${country}`);
			return response.data;
		} catch (error) {
			console.error(`Error removing market from campaign ${campaignId}:`, error);
			throw error;
		}
	},

	// Mark block as finished (sets status to Complete)
	markAsFinished: async (campaignId: string, blockId: string): Promise<BlockData> => {
		try {
			const response = await axiosInstance.put<BlockResponse>(`/campaign/${campaignId}/blocks/${blockId}`, {
				status: BlockStatus.COMPLETE,
			});
			const updatedBlockData: BlockData = {
				...response.data,
				key: response.data.id,
			};
			return updatedBlockData;
		} catch (error) {
			console.error(`Error marking block ${blockId} as finished:`, error);
			throw new Error("Failed to mark block as finished");
		}
	},
};
